import { useState } from "react";
import { Check, CircleCheckBig, Plus, RotateCcw, Trash2, X } from "lucide-react";

import { useBills, type NewBill } from "@/lib/hooks";
import { BILL_CATEGORIES, type Bill, type BillCategory } from "@/lib/meta";
import { money, relativeDay, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export function BillsPage() {
  const { bills, addBill, setPaid, deleteBill } = useBills();
  const [adding, setAdding] = useState(false);

  const total = bills.reduce((s, b) => s + b.amount, 0);
  const paidTotal = bills.filter((b) => b.paid).reduce((s, b) => s + b.amount, 0);
  const remaining = total - paidTotal;

  const unpaid = bills.filter((b) => !b.paid).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const paid = bills.filter((b) => b.paid).sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="This month" value={money(total)} />
        <Stat label="Paid" value={money(paidTotal)} accent="text-emerald-600 dark:text-emerald-400" />
        <Stat label="Remaining" value={money(remaining)} accent={remaining > 0 ? "text-amber-600 dark:text-amber-400" : undefined} />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{unpaid.length > 0 ? `${unpaid.length} bill${unpaid.length === 1 ? "" : "s"} to pay` : "All caught up"}</h3>
        <Button size="sm" onClick={() => setAdding((v) => !v)}>
          {adding ? <X /> : <Plus />}
          {adding ? "Cancel" : "Add bill"}
        </Button>
      </div>

      {adding && (
        <AddBillForm
          onCancel={() => setAdding(false)}
          onSubmit={async (b) => {
            await addBill(b);
            setAdding(false);
          }}
        />
      )}

      <div className="space-y-2">
        {unpaid.length === 0 && !adding && (
          <p className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <CircleCheckBig className="size-4 text-emerald-500" /> Every bill is paid this month.
          </p>
        )}
        {unpaid.map((b) => (
          <BillRow key={b.id} bill={b} onPay={() => setPaid(b.id, true)} onUnpay={() => setPaid(b.id, false)} onDelete={() => deleteBill(b.id)} />
        ))}
      </div>

      {paid.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Paid</h4>
          {paid.map((b) => (
            <BillRow key={b.id} bill={b} onPay={() => setPaid(b.id, true)} onUnpay={() => setPaid(b.id, false)} onDelete={() => deleteBill(b.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-3 md:p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={cn("mt-1 text-lg font-bold tabular-nums md:text-xl", accent)}>{value}</div>
      </CardContent>
    </Card>
  );
}

function BillRow({ bill, onPay, onUnpay, onDelete }: { bill: Bill; onPay: () => void; onUnpay: () => void; onDelete: () => void }) {
  const cat = BILL_CATEGORIES[bill.category];
  return (
    <div className={cn("group flex items-center gap-3 rounded-lg border bg-card p-3", bill.paid && "opacity-60")}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("truncate font-medium", bill.paid && "line-through")}>{bill.name}</span>
          <Badge variant="outline" className={cn("border-0", cat.chip)}>
            {cat.label}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">Due {relativeDay(bill.dueDate)}</div>
      </div>
      <div className="text-right font-semibold tabular-nums">{money(bill.amount)}</div>
      {bill.paid ? (
        <Button variant="ghost" size="icon-sm" onClick={onUnpay} aria-label="Mark unpaid" className="text-muted-foreground">
          <RotateCcw />
        </Button>
      ) : (
        <Button variant="outline" size="sm" onClick={onPay} className="gap-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400">
          <Check /> Pay
        </Button>
      )}
      <Button variant="ghost" size="icon-sm" onClick={onDelete} aria-label="Delete bill" className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
        <Trash2 />
      </Button>
    </div>
  );
}

function AddBillForm({ onSubmit, onCancel }: { onSubmit: (b: NewBill) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState(todayISO());
  const [category, setCategory] = useState<BillCategory>("utilities");
  const [recurrence, setRecurrence] = useState<"monthly" | "yearly">("monthly");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSubmit({ name: name.trim(), amount: parseFloat(amount) || 0, dueDate, category, recurrence });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Input placeholder="Bill name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <Input type="number" inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as BillCategory)}
            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            {Object.entries(BILL_CATEGORIES).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <select
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value as "monthly" | "yearly")}
            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !name.trim()}>
            Add bill
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
